import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Boxes, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type { CaseBucket, CaseRow } from '@/lib/cases-list/types';
import { InfiniteScrollSentinel } from '@/lib/pagination/infinite-scroll-sentinel';
import { declareSourceState } from '@/routes/declare/declare-route.state';
import { FilterBar } from './filters';
import { CaseRowItem } from './case-row';
import { useCasesList } from './use-cases-list';

// /cases route per docs/ui/operator-ui-tds.md §4.1.
//
// Three buckets (open / in-progress / needs-attention) rendered as a tab-style
// picker above the filter bar. URL search params hold the airport / status /
// date filters; bucket is component-local state — it's part of the visual nav,
// not a sharable filter.
//
// The list is cursor-paginated + infinite-scrolled (frontend-conventions §7.5):
// the leaf list is its OWN bounded scroll region (§18.1 — no page scroll), and a
// sentinel at its bottom drives `fetchNextPage` as the operator scrolls.
//
// The header's action opens the ONE declare flow (unified-declare §1a, decision 1:
// «Nuevo caso» is absorbed — a flight's cases come from declaring it; loose PNRs
// are a source inside the same flow).

/** Bucket order + the i18n key of each label (copy lives in `cases.list.bucket.*`). */
const BUCKETS: { value: CaseBucket; labelKey: string }[] = [
  { value: 'open', labelKey: 'list.bucket.open' },
  { value: 'in-progress', labelKey: 'list.bucket.inProgress' },
  { value: 'needs-attention', labelKey: 'list.bucket.needsAttention' },
];

export function CasesRoute() {
  const { t } = useTranslation('cases');
  const [bucket, setBucket] = useState<CaseBucket>('open');
  const cases = useCasesList({ bucket });

  return (
    <main className="density-page flex h-full min-h-0 w-full flex-col gap-4 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('list.subtitle')}</p>
        </div>
        <Button asChild>
          <Link to="/express" state={declareSourceState('BOARD')} data-testid="cases-declare">
            <Plus className="mr-1 h-3 w-3" aria-hidden />
            {t('list.declare')}
          </Link>
        </Button>
      </header>

      <nav aria-label={t('list.bucketsNav')} className="flex gap-1 rounded-md bg-muted/40 p-1 text-sm">
        {BUCKETS.map((b) => (
          <button
            key={b.value}
            type="button"
            onClick={() => setBucket(b.value)}
            className={cn(
              'rounded-sm px-3 py-1.5 transition-colors',
              bucket === b.value
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-state={bucket === b.value ? 'active' : 'inactive'}
          >
            {t(b.labelKey)}
          </button>
        ))}
      </nav>

      <FilterBar />

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <CasesListBody
            isLoading={cases.isLoading}
            isError={cases.isError}
            rows={cases.items}
            hasNextPage={cases.hasNextPage}
            isFetchingNextPage={cases.isFetchingNextPage}
            fetchNextPage={cases.fetchNextPage}
          />
        </CardContent>
      </Card>
    </main>
  );
}

interface CasesListBodyProps {
  isLoading: boolean;
  isError: boolean;
  rows: CaseRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function CasesListBody({
  isLoading,
  isError,
  rows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: CasesListBodyProps) {
  const { t } = useTranslation('cases');

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-6 text-sm text-destructive">{t('list.loadError')}</div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title={t('list.empty')}
        />
      </div>
    );
  }
  // The list is the ONLY scroll region (§18.1) — bounded, independent of the
  // page. The sentinel + loading row live INSIDE it so they cross into view as
  // the operator scrolls this region.
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="cases-scroll-region">
      <ul className="divide-y divide-border" data-testid="cases-list">
        {rows.map((row) => (
          <li key={row.caseUrn}>
            <CaseRowItem row={row} />
          </li>
        ))}
      </ul>
      {isFetchingNextPage ? (
        <div
          className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground"
          data-testid="cases-loading-more"
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          {t('list.loadingMore')}
        </div>
      ) : null}
      <InfiniteScrollSentinel
        onReachEnd={fetchNextPage}
        disabled={!hasNextPage || isFetchingNextPage}
      />
    </div>
  );
}
